import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientPreview } from "../../../redux/slices/dashSlice";
import {
  PersonOutlined,
  EmailOutlined,
  PhoneOutlined,
  DescriptionOutlined,
  LocationCityOutlined,
  BusinessOutlined,
  FormatListNumberedOutlined,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import LoadingIndicator from "../../utils/LoadingIndicator";

const ClientPreview = forwardRef(
  ({ search, role, user, from, to, month, year, selectedRegion, selectedState, selectedChannels}, ref) => {
    const dispatch = useDispatch();
    const { clientPreview, totalCount, getStatus } = useSelector(
      (state) => state.dash,
    );

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    useEffect(() => {
      dispatch(
        fetchClientPreview({
          page: page + 1,
          rowsPerPage,
          search: search,
          role: role,
          user: user,
          from: from,
          to: to,
          month: from || to ? null : month,
          year: from || to ? null : year,
          selectedRegion: selectedRegion,
          selectedState: selectedState,
          selectedChannels:selectedChannels,
        }),
      );
    }, [
      dispatch,
      page,
      rowsPerPage,
      search,
      role,
      from,
      to,
      user,
      month,
      year,
      selectedRegion,
      selectedState,
      selectedChannels
    ]);

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    useImperativeHandle(ref, () => ({
      downloadExcel: () => {
        if (!clientPreview || clientPreview.length === 0) return;

        const dataToExport = clientPreview.map((client, index) => ({
          "S.No.": index + 1,
          "Client Name": client.clientname,
          "User Name": client.usersName,
          Email: client.email,
          Phone: client.phone,
          Quotations: client.project_count,
          City: client.city,
          "Architect Firm": client.afd,
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Clients");
        XLSX.writeFile(wb, "ClientPreview.xlsx");
      },
    }));

    return (
      <>
        <LoadingIndicator show={getStatus === "pending"} />

        <Box sx={{ width: "100%" }}>
          <Paper sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-label="client table">
                <TableHead sx={{ background: "#04121f", color: "white" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FormatListNumberedOutlined /> S. No.
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonOutlined /> Client Name
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonOutlined /> User Name
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailOutlined /> Email
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneOutlined /> Phone
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionOutlined /> Quotations
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationCityOutlined /> City
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessOutlined /> Architect Firm
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientPreview?.length > 0 ? (
                    clientPreview.map((client, index) => (
                      <TableRow
                        key={`${client.clientname}-${index}-${client.usersName}-${client.email}-${client.project_count}`}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                          "&:hover": { backgroundColor: "#eef2ff" },
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <TableCell
                          sx={{ fontWeight: "bold", color: "#4c66f5" }}
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {client.clientname}
                        </TableCell>
                        <TableCell>{client.usersName}</TableCell>
                        <TableCell>{client.email}</TableCell>
                        <TableCell>{client.phone}</TableCell>
                        <TableCell>{client.project_count}</TableCell>
                        <TableCell>{client.city}</TableCell>
                        <TableCell>{client.afd}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          sx={{ py: 2 }}
                        >
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, totalCount || 0]}
              component="div"
              count={totalCount || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      </>
    );
  },
);

export default ClientPreview;
